import React, { useEffect, useMemo, useRef } from "react";
import { FPS, reelDurationFrames, speechDurationMs } from "@hudhud/reel";
import { duckEnvelope, openerHoldMs, timelineMusicMutes, type Clip } from "@hudhud/core";
import { useSfxLibrary } from "../components/SfxPick.js";
import type { ClipFiles } from "../api.js";
import { bedPositionSec, clockJumped, createMixGraph, mixGains, readMeters, sfxEventsOf, wireElement, type Audition, type MixGraph, type MixInput, type SfxEvent } from "./liveMix.js";

const noop = () => undefined;

/**
 * The sound of a preview whose picture makes none: the pieces already cut into one file carry the voice at the clip's level
 * (their clock is the speech's own), the music bed and the effects play beside it, all through the studio's faders and meters.
 * `clock` is the picture's time in ms, written by whoever draws it; every frame the sound follows it. For the seconds after a
 * cut edit, until the server has cut again, the voice trails the timeline — the picture is drawn from the new cuts at once.
 */
export const LiveSound: React.FC<{ clip: Clip; files: ClipFiles; playing: boolean; speed: number; audition: Audition; clock: React.RefObject<number> }> = ({ clip, files, playing, speed, audition, clock }) => {
  const sounds = useSfxLibrary();
  const hold = openerHoldMs(clip);
  const speechMs = speechDurationMs(clip);
  const totalMs = (reelDurationFrames(clip) / FPS) * 1000;
  const env = useMemo(() => duckEnvelope(clip.captions.flatMap((c) => c.words), { duckDb: clip.music.duckDb }), [clip.captions, clip.music.duckDb]);
  const mutes = useMemo(() => timelineMusicMutes(clip), [clip]);
  const sfxEvents = useMemo(() => sfxEventsOf(clip, sounds, hold, speechMs), [clip, sounds, hold, speechMs]);
  const sfxUrls = useMemo(() => [...new Set(sfxEvents.map((e) => e.url))], [sfxEvents]);
  const bedNet = clip.music.trackId ? `/assets/music/${clip.music.trackId}` : null;
  const voiceUrl = files.timelineLite?.url ?? null;
  const voiceEl = useRef<HTMLVideoElement>(null), bedEl = useRef<HTMLAudioElement>(null);
  const sfxEls = useRef(new Map<string, HTMLAudioElement>());
  const graph = useRef<MixGraph | null>(null);
  const prevMs = useRef(0);
  // what the clock reads each frame, without restarting it on every edit
  const mix = useRef<MixInput>({ clip, hold, speechMs, totalMs, env, mutes, audition });
  mix.current = { clip, hold, speechMs, totalMs, env, mutes, audition };
  const events = useRef<SfxEvent[]>(sfxEvents); events.current = sfxEvents;
  const bed = useRef(bedNet); bed.current = bedNet;

  /** The graph is built on the first play (a click has happened by then, so it may sound) and every element is wired once. */
  const ensureGraph = () => {
    graph.current ??= createMixGraph();
    const g = graph.current;
    if (!g) return;
    if (voiceEl.current) wireElement(g, voiceEl.current, "voice");
    if (bedEl.current) wireElement(g, bedEl.current, "bed");
    for (const el of sfxEls.current.values()) wireElement(g, el, "sfx");
    if (g.ctx.state !== "running") g.ctx.resume().catch(noop);
  };
  const fireSfx = (e: SfxEvent) => {
    const el = sfxEls.current.get(e.url);
    if (!el) return;
    const g = graph.current?.sfx.get(el);
    if (g) g.gain.value = e.gain; else el.volume = Math.min(1, e.gain);
    el.playbackRate = mix.current.clip.sfx.pitch;
    try { el.currentTime = 0; } catch { /* not seekable yet */ }
    el.play().catch(noop);
  };
  /**
   * Keeps the voice and the bed in step with the picture at `ms`, and sets this frame's gains and meters. `jumped`: the picture
   * looped or was moved while playing, so the bed, which otherwise runs on its own, is put where the picture now is.
   */
  const follow = (ms: number, going: boolean, jumped = false) => {
    const m = mix.current;
    const inSpeech = ms >= m.hold && ms < m.hold + m.speechMs;
    const v = voiceEl.current;
    if (v) {
      if (going && inSpeech) {
        const want = (ms - m.hold) / 1000;
        if (Math.abs(v.currentTime - want) > 0.12) { try { v.currentTime = want; } catch { /* not seekable yet */ } }
        if (v.paused) v.play().catch(noop);
      } else if (!v.paused) v.pause();
    }
    const b = bedEl.current;
    if (bed.current && b) {
      const start = m.clip.music.startMs / 1000, end = m.clip.music.endMs != null ? m.clip.music.endMs / 1000 : b.duration || 0;
      const place = () => { try { b.currentTime = bedPositionSec(m.clip.music, ms, b.duration); } catch { /* not seekable yet */ } };
      if (going) {
        if (b.paused) { place(); b.play().catch(noop); }
        else if (jumped) place();
        else if (end > start && b.currentTime >= end - 0.05) b.currentTime = start;
      } else if (!b.paused) b.pause();
    }
    const g = graph.current;
    if (g) { const gains = mixGains(m, ms, m.clip.voiceMuted ? 0 : 1, inSpeech); g.voice.gain.value = gains.voice; g.bed.gain.value = gains.bed; }
    readMeters(g, going);
  };

  // the clock: each frame the sound follows the picture, the cues just passed fire, the meters read
  useEffect(() => {
    if (!playing) { prevMs.current = clock.current ?? 0; follow(clock.current ?? 0, false); return; }
    ensureGraph();
    let raf = 0;
    const tick = () => {
      const ms = clock.current ?? 0;
      const prev = prevMs.current; prevMs.current = ms;
      // a jump never fires the cues it skipped
      if (ms > prev && ms - prev < 1500 && mix.current.audition !== "music") for (const e of events.current) if (e.atMs > prev && e.atMs <= ms) fireSfx(e);
      follow(ms, true, clockJumped(prev, ms));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, voiceUrl]);
  useEffect(() => { for (const el of [voiceEl.current, bedEl.current]) if (el) el.playbackRate = speed; }, [speed, voiceUrl]);
  // a fader moved while paused still sets the gains, so the next play starts at the new level
  useEffect(() => { if (!playing) follow(clock.current ?? 0, false); }, [clip.voiceDb, clip.voiceMuted, clip.music, audition]);

  return (
    <>
      {voiceUrl ? <video ref={voiceEl} src={voiceUrl} preload="auto" playsInline style={{ display: "none" }} /> : null}
      {bedNet ? <audio ref={bedEl} src={bedNet} preload="auto" /> : null}
      {sfxUrls.map((u) => <audio key={u} src={u} preload="auto" ref={(el) => { if (el) sfxEls.current.set(u, el); else sfxEls.current.delete(u); }} />)}
    </>
  );
};
